import { ajvResolver } from '@hookform/resolvers/ajv';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Grid, Typography } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { cloneDeep, entries, flatMap, isEmpty } from 'lodash';
import { Controller, Path, SubmitHandler, useForm } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { useEditComponentPageContext } from 'src/components/context/components/EditComponentPageContext';
import { Drawer } from 'src/components/drawer';
import { SidebarHeader } from 'src/components/header';
import {
  NumberInputWidget,
  ResourceSelectorSidebar,
  SelectDropdownWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { useGetProducts, useGetThemes } from 'src/hooks';
import { type IGcfComponentConfig } from 'src/interfaces';
import { lang } from 'src/utils';
import { handleComponentConfigChange } from 'src/utils/changeHandler';

import { InfoBox } from '../../modules/InfoBox';
import { useThemeOptions } from '../FormSelector.hook';
import { CommonFormProps } from '../FormSelector.interface';
import { FormWrapper } from '../FormWrapper';
import { HeaderTextStylingAccordion } from '../modules/headerTextStylingAccordion/HeaderTextStylingAccordion';
import { HeaderTextStylingOptions } from '../modules/headerTextStylingAccordion/HeaderTextStylingAccordion.interface';
import { ProductsDrawer } from '../modules/productsDrawer';
import { CustomImageSource } from './customImageSource/CustomImageSource';
import { useLOVForm } from './ListingWithOptionalVisuals.hook';
import {
  GcfListingWithOptionalVisualsConfigInterface,
  GcfListingWithOptionalVisualsConfigSchema,
  ListingWithOptionalVisualsColumnStyle,
  OptionalVisualImageSrcEnum,
  SelectHorizontalOptions,
  SelectVerticalOptions,
} from './ListingWithOptionalVisuals.interface';
import {
  SidebarContainer,
  SidebarContent,
} from './ListingWithOptionalVisuals.styled';
import { SelectableImageSource } from './selectableImageSource/SelectableImageSource';

export const ListingWithOptionalVisualsForm = (props: CommonFormProps) => {
  const {
    control,
    handleSubmit: submitWrapper,
    reset,
    watch,
    formState,
    getValues,
    setValue,
  } = useForm<IGcfComponentConfig>({
    values: cloneDeep(props.componentDetails.config),
    resolver: ajvResolver(
      GcfListingWithOptionalVisualsConfigSchema as unknown as JSONSchemaType<GcfListingWithOptionalVisualsConfigInterface>,
    ),
  });

  const showDefaultVisuals =
    watch('productList.config.visuals.visualsSource') === 'lineup';
  const showSelectedVisuals =
    watch('productList.config.visuals.visualsSource') === 'selected';
  const showCustomVisuals =
    watch('productList.config.visuals.visualsSource') === 'custom';
  const productList = watch('productList');
  const numberOfVisuals =
    watch('productList.config.visuals.visualsLength') || 3;
  const showVisuals = watch('productList.config.visuals.visible');
  const productCodes = [
    ...new Set(
      flatMap(
        entries(productList?.groups).map(([, value]) =>
          entries(value.products).map(
            ([, productsValue]) => productsValue.productCode,
          ),
        ),
      ),
    ),
  ].sort();

  const { data: products = [] } = useGetProducts(
    !isEmpty(productCodes)
      ? [`product_code.list=${productCodes.join('|')}`]
      : [],
  );

  const {
    openBackgroundDrawer,
    openTitleDrawer,
    openSettingsDrawer,
    openImageSelectionDrawer,
    openProductsDrawer,
    isDefaultImageSourceUpdated,
    onToggleBackgroundDrawer,
    onToggleTitleDrawer,
    onToggleSettingsDrawer,
    onToggleImageSelectionDrawer,
    onToggleProductsDrawer,
    onToggleDefaultImageUpdated,
  } = useLOVForm();

  const { data: themes = [] } = useGetThemes();
  const { handleChangeComponentDetails } = useEditComponentPageContext();
  const themeOptions = useThemeOptions();

  const handleSubmit: SubmitHandler<IGcfComponentConfig> = (
    value: typeof props.componentDetails.config,
  ) => {
    props.onSave(value);
  };

  const handleOnChange = (
    key: Path<IGcfComponentConfig> | Path<IGcfComponentConfig>[],
    value: unknown,
    fieldOnChange?: any,
  ) => {
    props.onChange(props.componentDetails.config ?? {}, key, value);
    fieldOnChange && fieldOnChange(value);
  };

  const handleMultipleChange = (
    keys: string[],
    value: unknown[],
    fieldOnChange?: (...event: any[]) => void,
  ) => {
    props.onChange(props.componentDetails.config ?? {}, keys, value);
    if (fieldOnChange) {
      Array.isArray(value) ? fieldOnChange(value[0]) : fieldOnChange(value);
    }
  };

  const headerTextStyleHasErrors = () => {
    if (
      formState.errors.header ||
      formState.errors.disclaimer ||
      formState.errors.priceLabel
    )
      return true;
    else return false;
  };

  const handleThemeChange = (themeId: unknown) => {
    const theme = themes.find((theme) => theme.id === themeId);

    if (theme) {
      handleChangeComponentDetails([themeId, theme], ['theme_id', 'theme']);
    }
  };
  const hasBackgroundImage = (mop?: { background?: string }) =>
    typeof mop?.background === 'string' && mop.background.trim() !== '';

  const items = [
    {
      label: lang.componentSettings,
      content: <div />,
      onLabelClick: onToggleSettingsDrawer,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: false,
    },
    {
      label: lang.background,
      onLabelClick: onToggleBackgroundDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: props.hasTriedSaving
        ? !hasBackgroundImage(props.componentDetails.config)
        : false,
    },
    {
      label: lang.headerTextStyling,
      onLabelClick: onToggleTitleDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: headerTextStyleHasErrors(),
    },
    {
      label: lang.products,
      onLabelClick: onToggleProductsDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: false,
    },
    {
      label: lang.imageSelection,
      content: <div />,
      onLabelClick: onToggleImageSelectionDrawer,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: false,
    },
  ];

  return (
    <FormWrapper
      formState={formState}
      formReset={reset}
      formGetValues={getValues}
      onSave={submitWrapper(handleSubmit)}>
      <SidebarAccordion items={items} />
      <Controller
        name="background"
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => (
          <ResourceSelectorSidebar
            label={lang.background}
            ref={ref}
            type={ResourceType.BACKGROUND}
            defaultAssetName={field.value}
            openResourceSelectorDrawer={openBackgroundDrawer}
            onToggleResourceSelectorDrawer={onToggleBackgroundDrawer}
            onChange={(value) =>
              handleComponentConfigChange(
                props,
                'background',
                value,
                field.onChange,
              )
            }
            hasError={!!fieldState.error}
            error={fieldState.error?.message || false}
          />
        )}
      />

      <Drawer open={openTitleDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.headerTextStyling}
            onBackClick={onToggleTitleDrawer}
          />
          <SidebarContent>
            <HeaderTextStylingAccordion
              componentConfig={props.componentDetails.config}
              handleOnChange={handleOnChange}
              errors={formState?.errors}
              enableIndividualStyles={false}
              theme={props.componentDetails.theme}
              hidden={[
                HeaderTextStylingOptions.HEADER_LAYOUT,
                HeaderTextStylingOptions.MOLECULE_DISPLAY_LABEL,
              ]}
            />
          </SidebarContent>
        </SidebarContainer>
      </Drawer>

      <Drawer open={openImageSelectionDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.imageSelection}
            onBackClick={onToggleImageSelectionDrawer}
          />
          <SidebarContent>
            <Grid container spacing={2} style={{ padding: '26px' }}>
              <Grid item xs={12}>
                <Controller
                  name="productList.config.visuals.visible"
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <SwitchButtonWidget
                      {...field}
                      ref={ref}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'productList.config.visuals.visible',
                          value,
                          field.onChange,
                        )
                      }></SwitchButtonWidget>
                  )}
                />
              </Grid>
              {showVisuals && (
                <>
                  <Grid item xs={8} pb={2.5}>
                    <Controller
                      name="productList.config.visuals.visualsLength"
                      control={control}
                      defaultValue={3}
                      render={({ field: { ref, ...field } }) => (
                        <NumberInputWidget
                          label={lang.numberOfImages}
                          key={numberOfVisuals}
                          {...field}
                          ref={ref}
                          options={{ max: 3, min: 1 }}
                          onChange={(value) => {
                            const numericValue = Number(value);

                            if (Number.isNaN(numericValue)) return;

                            const finalValue = Math.min(
                              3,
                              Math.max(1, numericValue),
                            );

                            field.onChange(finalValue);

                            handleComponentConfigChange(
                              props,
                              'productList.config.visuals.visualsLength',
                              finalValue,
                            );
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography>{lang.imageAlignment}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="productList.config.visuals.horizontalAlign"
                      control={control}
                      render={({ field: { ref, ...field } }) => (
                        <SelectDropdownWidget
                          {...field}
                          ref={ref}
                          label={lang.horizontal}
                          selectOptions={SelectHorizontalOptions}
                          onChange={(value) =>
                            handleComponentConfigChange(
                              props,
                              'productList.config.visuals.horizontalAlign',
                              value,
                              field.onChange,
                            )
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="productList.config.visuals.verticalAlign"
                      control={control}
                      render={({ field: { ref, ...field } }) => (
                        <SelectDropdownWidget
                          {...field}
                          ref={ref}
                          label={lang.vertical}
                          selectOptions={SelectVerticalOptions}
                          onChange={(value) =>
                            handleComponentConfigChange(
                              props,
                              'productList.config.visuals.verticalAlign',
                              value,
                              field.onChange,
                            )
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="productList.config.visuals.visualsSource"
                      control={control}
                      render={({ field: { ref, ...field } }) => (
                        <SelectDropdownWidget
                          {...field}
                          ref={ref}
                          label={lang.imageSource}
                          selectOptions={[
                            {
                              label: 'Default',
                              value: OptionalVisualImageSrcEnum.lineup,
                            },
                            {
                              label: 'Selectable',
                              value: OptionalVisualImageSrcEnum.selected,
                            },
                            {
                              label: 'Custom',
                              value: OptionalVisualImageSrcEnum.custom,
                            },
                          ]}
                          onChange={(value) => {
                            handleMultipleChange(
                              ['productList.config.visuals.visualsSource'],
                              [value],
                              field.onChange,
                            );
                            setValue('productList.config.visuals.items', []);
                            onToggleDefaultImageUpdated(
                              value === OptionalVisualImageSrcEnum.lineup
                                ? false
                                : undefined,
                            );
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {(showSelectedVisuals || showDefaultVisuals) && (
                    <Grid item xs={12}>
                      <Controller
                        name="productList.config.visuals.items"
                        control={control}
                        render={({ field: { ref, ...field } }) => (
                          <SelectableImageSource
                            {...field}
                            ref={ref}
                            productList={
                              props?.componentDetails?.config?.productList
                            }
                            products={products}
                            onChange={(value, setChanged) => {
                              handleComponentConfigChange(
                                props,
                                'productList',
                                value,
                                field.onChange,
                              );
                              if (setChanged && !isDefaultImageSourceUpdated) {
                                onToggleDefaultImageUpdated(setChanged);
                              }
                            }}
                            isDefaultProductUpdated={
                              isDefaultImageSourceUpdated
                            }
                            isDefaultImageSource={showDefaultVisuals}
                            visualsLength={numberOfVisuals}
                          />
                        )}
                      />
                    </Grid>
                  )}

                  {showCustomVisuals && (
                    <Grid item xs={12}>
                      <Controller
                        name="images"
                        control={control}
                        render={({ field: { ref, ...field } }) => (
                          <CustomImageSource
                            {...field}
                            ref={ref}
                            onChange={(value) => {
                              handleComponentConfigChange(
                                props,
                                'images',
                                value,
                                field.onChange,
                              );
                            }}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </SidebarContent>
        </SidebarContainer>
      </Drawer>

      <Drawer open={openSettingsDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.componentSettings}
            onBackClick={onToggleSettingsDrawer}
          />
          <SidebarContent>
            <Grid container spacing={2} style={{ padding: '26px' }}>
              <Grid item xs={12}>
                <Controller
                  name="isFlavourCycling"
                  control={control}
                  defaultValue={false}
                  render={({ field: { ref, ...field } }) => (
                    <SwitchButtonWidget
                      {...field}
                      label={lang.displayFlavorCycling}
                      ref={ref}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'isFlavourCycling',
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="showComponentProductDisclaimer"
                  control={control}
                  defaultValue={false}
                  render={({ field: { ref, ...field } }) => (
                    <SwitchButtonWidget
                      {...field}
                      label={lang.showProductDisclaimers}
                      ref={ref}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'showComponentProductDisclaimer',
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectDropdownWidget
                  label={lang.selectTheme}
                  selectOptions={themeOptions}
                  value={props.componentDetails?.theme_id}
                  onChange={handleThemeChange}
                />
                <InfoBox>{lang.changeThemeInfoText}</InfoBox>
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="lineupColumns"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <NumberInputWidget
                      {...field}
                      ref={ref}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      label={lang.columns}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          `lineupColumns`,
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="columnStyle"
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <SelectDropdownWidget
                      {...field}
                      ref={ref}
                      label={lang.columnStyle}
                      selectOptions={[
                        {
                          label: 'Continuous',
                          value:
                            ListingWithOptionalVisualsColumnStyle.continuous,
                        },
                        {
                          label: 'Category',
                          value: ListingWithOptionalVisualsColumnStyle.category,
                        },
                      ]}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'columnStyle',
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="marginBottom"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <NumberInputWidget
                      {...field}
                      ref={ref}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      label="Product lineup space"
                      options={{ max: 999, min: 0 }}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          `marginBottom`,
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </SidebarContent>
        </SidebarContainer>
      </Drawer>

      <ProductsDrawer
        open={openProductsDrawer}
        componentDetails={props.componentDetails}
        handleOnChange={handleMultipleChange}
        onClose={onToggleProductsDrawer}
      />
    </FormWrapper>
  );
};
